import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FeatureToggleEnum, TARIFF_CHANGE_EMOBILITY_ROUTE, TARIFF_CHANGE_PRODUCTS_ROUTE, TARIFF_CHANGE_ROUTE } from '@mwe/constants';
import { Router } from '@angular/router';
import { createAddress, IPopupModelV2, ModalChild, Product, Sparte } from '@mwe/models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProfileService, TariffChangeStateService } from '@mwe/services';
import { TranslatePipe } from '@ngx-translate/core';
import { BadgeComponent } from '../badge/badge/badge.component';
import { ClickableProductsCardComponent } from '../cards/clickable-products-card/clickable-products-card.component';
import { ModalComponent } from '../modal/modal.component';
import { containsEmobility, containsFernwaerme, containsStromOrGasCategory, getCompanyCorrelationFromCategory } from '@mwe/utils';

@Component({
  selector: 'mwe-tariff-change-selection-popup',
  templateUrl: './tariff-change-selection-popup.component.html',
  imports: [BadgeComponent, ClickableProductsCardComponent, ModalComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffChangeSelectionPopupComponent implements ModalChild {
  private readonly router = inject(Router);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly profileService = inject(ProfileService);
  private readonly tariffChangeStateService = inject(TariffChangeStateService);

  loadedData: { availableCategories: string[]; products: Product[] }; // data passed by popupService
  Sparte = Sparte;

  readonly popupModel = signal<IPopupModelV2>(null);

  readonly stromCompany = computed<string>(() => getCompanyCorrelationFromCategory(Sparte.Strom));
  readonly emobilityCompany = computed<string>(() => getCompanyCorrelationFromCategory(Sparte.Emobility));
  readonly isEmobilityDisabled = computed<boolean>(
    () => !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.CHARGING_CARD_TARIFF_CHANGE_ENABLED),
  );
  readonly hasStromGas = computed<boolean>(() => {
    const categories = this.loadedData?.availableCategories ?? [];
    return containsStromOrGasCategory(categories) || containsFernwaerme(categories);
  });
  readonly hasEmobility = computed<boolean>(() => {
    const categories = this.loadedData?.availableCategories ?? [];
    return containsEmobility(categories);
  });

  startStromGasTariffChange(): void {
    this.closePopup();
    if (this.loadedData.products) {
      this.tariffChangeStateService.directAddressSelection = true;
      this.tariffChangeStateService.tariffChangeAddress = createAddress(this.loadedData.products[0].address);
      this.tariffChangeStateService.tariffChangeProducts = this.loadedData.products;
      this.router.navigateByUrl(`/${TARIFF_CHANGE_ROUTE}/${TARIFF_CHANGE_PRODUCTS_ROUTE}`);

      return;
    }

    this.router.navigateByUrl(`/${TARIFF_CHANGE_ROUTE}`);
  }

  startEmobilityTariffChange(): void {
    this.closePopup();

    // FT active -> new emobility tariff change route, FT inactive -> old S/G tariff change wizard
    const route = this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.CHARGING_CARD_TARIFF_CHANGE_ENABLED)
      ? TARIFF_CHANGE_EMOBILITY_ROUTE
      : TARIFF_CHANGE_ROUTE;
    this.router.navigateByUrl(`/${route}`);
  }

  closePopup(): void {
    this.activeModal.close();
  }
}
